'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from './api';
import { useAuth } from './auth';

/**
 * Könyvjelző (szerver-oldali) — egy publikáción belüli, indexalapú jelzés.
 *
 * A korábbi (M1) Zustand-Persist „localStorage-ban tárolt könyvjelzőzés"
 * helyett ez a modul most az `annotation-svc` `/me/bookmarks/{pubId}`
 * végpontjaival dolgozik — szerver-oldali, eszközök közti szinkronizációval.
 * Mock-fallback NINCS: bejelentkezetlenül a hookok üres listát adnak vissza,
 * a `toggle`/`remove` no-op (a `useBookmarks` `enabled` flag-jén keresztül).
 */
export interface Bookmark {
  /** Reading-order szakasz-index — a manifest readingOrder-ön belüli pozíció. */
  index: number;
  /** A szakasz emberbarát címe (TOC vagy fájlnév-alapú). */
  title: string;
  /** Szerver-oldali felvétel időbélyege ISO-ban. */
  addedAt: string;
}

const BookmarkSchema = z.object({
  publicationId: z.string().uuid(),
  sectionIndex: z.number().int().nonnegative(),
  title: z.string().min(1),
  addedAt: z.string().datetime({ offset: true }),
});

const BookmarkListSchema = z.object({
  data: z.array(BookmarkSchema),
});

const ANNOTATION_BASE = '/api/annotation';

const bookmarksKey = (publicationId: string) => ['bookmarks', publicationId] as const;
const allBookmarksKey = ['bookmarks', 'me'] as const;

/** A felhasználó ÖSSZES könyvjelzője — a /shelf csoportosítja publikációkra. */
export function useAllBookmarks() {
  const isLogged = useAuth((s) => s.isAuthenticated());
  return useQuery({
    queryKey: allBookmarksKey,
    queryFn: async () => {
      const res = await apiRequest(BookmarkListSchema, '/me/bookmarks', {
        baseUrl: ANNOTATION_BASE,
      });
      return res.data;
    },
    enabled: isLogged,
    staleTime: 30_000,
  });
}

function toUiBookmark(item: z.infer<typeof BookmarkSchema>): Bookmark {
  return {
    index: item.sectionIndex,
    title: item.title,
    addedAt: item.addedAt,
  };
}

/**
 * Egy publikáció könyvjelzőinek lekérése + UX-célú segédfüggvények.
 * Bejelentkezetlenül `enabled: false` — a mutation-ok no-op-ok.
 */
export function useBookmarks(publicationId: string) {
  const isLogged = useAuth((s) => s.isAuthenticated());
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: bookmarksKey(publicationId),
    queryFn: async () => {
      const res = await apiRequest(BookmarkListSchema, `/me/bookmarks/${publicationId}`, {
        baseUrl: ANNOTATION_BASE,
      });
      return res.data.map(toUiBookmark).sort((a, b) => a.index - b.index);
    },
    enabled: Boolean(publicationId) && isLogged,
    staleTime: 30_000,
  });

  const upsert = useMutation({
    mutationFn: (bm: { index: number; title: string }) =>
      apiRequest(BookmarkSchema, `/me/bookmarks/${publicationId}`, {
        method: 'PUT',
        baseUrl: ANNOTATION_BASE,
        body: { section_index: bm.index, title: bm.title },
      }),
    onMutate: async (bm) => {
      // Optimista frissítés — a UI azonnal kiemelt állapotban mutatja a könyvjelzőt.
      await qc.cancelQueries({ queryKey: bookmarksKey(publicationId) });
      const prev = qc.getQueryData<Bookmark[]>(bookmarksKey(publicationId));
      qc.setQueryData<Bookmark[]>(bookmarksKey(publicationId), (current) => {
        const list = current ?? [];
        if (list.some((b) => b.index === bm.index)) return list;
        return [...list, { index: bm.index, title: bm.title, addedAt: new Date().toISOString() }]
          .sort((a, b) => a.index - b.index);
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(bookmarksKey(publicationId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: bookmarksKey(publicationId) });
    },
  });

  const remove = useMutation({
    mutationFn: (index: number) =>
      apiRequest(z.unknown(), `/me/bookmarks/${publicationId}/${index}`, {
        method: 'DELETE',
        baseUrl: ANNOTATION_BASE,
      }),
    onMutate: async (index) => {
      await qc.cancelQueries({ queryKey: bookmarksKey(publicationId) });
      const prev = qc.getQueryData<Bookmark[]>(bookmarksKey(publicationId));
      qc.setQueryData<Bookmark[]>(bookmarksKey(publicationId), (current) =>
        (current ?? []).filter((b) => b.index !== index),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(bookmarksKey(publicationId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: bookmarksKey(publicationId) });
    },
  });

  const list: Bookmark[] = query.data ?? [];

  return {
    list,
    isLoading: query.isLoading && isLogged,
    isError: query.isError,
    has: (index: number) => list.some((b) => b.index === index),
    /** A jelölt szakaszt ki/be kapcsolja — szerver-oldali upsert/delete. */
    toggle: (bm: { index: number; title: string }) => {
      if (!isLogged) return; // nincs lokális fallback
      const existing = list.find((b) => b.index === bm.index);
      if (existing) remove.mutate(bm.index);
      else upsert.mutate(bm);
    },
    remove: (index: number) => {
      if (!isLogged) return;
      remove.mutate(index);
    },
  };
}
